import { CircleLoader, Grid, toast } from '@sparrowengg/twigs-react';
import React, { useEffect, useState, useCallback } from 'react';
import TopNav from '../components/TopNav';
import SideNav from '../components/SideNav';
import SmsListView from '../components/SmsListView';
import { getApiBaseUrl } from '../utils/environmentService';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { fetchEnvironments } from '../utils/environmentService';

function Home({ onEnvironmentCleared, environments, setEnvironments }) {
  const currentEnvironment = useSelector(state => state.environment.selectedEnvironment);
  
  // State declarations
  const [smsData, setSmsData] = useState([]); // Current page data
  const [filteredSmsData, setFilteredSmsData] = useState([]); // Filtered data for display
  const [activePage, setActivePage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50, // Hardcoded to 50
    hasNextPage: false,
    hasPrevPage: false
  });
  const [searchQuery, setSearchQuery] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [socket, setSocket] = useState(null);

  // Fetch SMS data from server with pagination
  const fetchData = useCallback(async(page = 1) => {
    try {
      setLoading(true);
      const envData = await fetchEnvironments();
      const apiBaseUrl = getApiBaseUrl(currentEnvironment, envData);
      
      if (!apiBaseUrl) {
        toast({
          variant: 'error',
          title: 'No Environment Selected',
          description: 'Please select an environment to continue',
        });
        return;
      }

      // Use hardcoded itemsPerPage of 50
      const itemsPerPage = 50;
      
      // Build URL with pagination parameters
      let url = `${apiBaseUrl}/sms?page=${page}&limit=${itemsPerPage}`;
      
      const response = await axios.get(url);
      let responseData = response.data;
      
      // Handle both old format (array) and new format (object with data and pagination)
      if (Array.isArray(responseData)) {
        // Old format - convert to new format for backward compatibility
        responseData = {
          data: responseData,
          pagination: {
            currentPage: 1,
            totalPages: 1,
            totalItems: responseData.length,
            itemsPerPage: responseData.length,
            hasNextPage: false,
            hasPrevPage: false
          }
        };
      }
      
      const { data, pagination } = responseData;
      
      if (Array.isArray(data)) {
        setSmsData(data);
        setPaginationInfo(pagination);
        setActivePage(pagination.currentPage);
      } else {
        toast({
          variant: 'error',
          title: 'Invalid data format',
          description: 'The backend did not return an array of messages.',
        });
        setSmsData([]);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        variant: 'error',
        title: 'Error fetching data',
        description: 'There was a problem getting data from the selected environment',
      });
    } finally {
      setLoading(false);
    }
  }, [currentEnvironment]);

  // Set up WebSocket connection
  useEffect(() => {
    const apiBaseUrl = getApiBaseUrl(currentEnvironment, environments);
    if (apiBaseUrl) {
      const newSocket = io(apiBaseUrl);
      setSocket(newSocket);

      newSocket.on('newSMS', () => {
        // Refresh current page when a new SMS is received
        fetchData(activePage);
      });

      return () => {
        newSocket.disconnect();
      };
    }
  }, [currentEnvironment, environments, fetchData, activePage]);

  // Handle SMS search - filter current page data client-side
  const handleSearch = (searchData) => {
    setSearchQuery(searchData);
    
    if (!searchData || searchData.trim() === '') {
      // If search is empty, show all data from current page
      setFilteredSmsData(smsData);
    } else {
      // Filter current page data based on search query
      const filtered = smsData.filter(sms => {
        return sms.message && typeof sms.message === 'string' && 
               sms.message.toLowerCase().includes(searchData.toLowerCase());
      });
      setFilteredSmsData(filtered);
    }
  };

  // Update filtered data whenever sms data changes
  useEffect(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      setFilteredSmsData(smsData);
    } else {
      const filtered = smsData.filter(sms => {
        return sms.message && typeof sms.message === 'string' && 
               sms.message.toLowerCase().includes(searchQuery.toLowerCase());
      });
      setFilteredSmsData(filtered);
    }
  }, [smsData, searchQuery]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setActivePage(newPage);
    fetchData(newPage);
  }, [fetchData]);

  // Fetch initial data when environment changes
  useEffect(() => {
    if (!currentEnvironment) {
      return;
    }
    fetchData(1);
  }, [currentEnvironment, environments, fetchData]);

  return (
    <Grid
      gap="$8"
      css={{
        width: '100%',
        height: '100%',
        'p': { fontFamily: 'DM Sans !important' }
      }}
      templateRows="60px 1fr"
    >
      <TopNav 
        activePage={activePage} 
        setActivePage={handlePageChange} 
        paginationInfo={paginationInfo}
        handleSearch={handleSearch}
        onRefresh={() => fetchData(activePage)}
        currentEnvironment={currentEnvironment}
      />

      <Grid templateColumns="225px 1fr">
        <SideNav setSmsData={setSmsData} />

        {!loading ? (
          <SmsListView data={filteredSmsData} activePage={activePage} />
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%' 
          }}>
            <CircleLoader size="3xl" color="primary" />
          </div>
        )}
      </Grid>
    </Grid>
  );
}

export default Home;